import { useCallback, useState } from 'react';
import { useContactFormController } from '@/components/services/contact/ContactForm';

interface UseAddProviderComposerOptions {
	houseId: number;
	onAdded: (contactId: number | null) => void;
}

/** Contact creation state for the Contact tab in the shared add composer. */
export const useAddProviderComposer = ({ houseId, onAdded }: UseAddProviderComposerOptions) => {
	const [selectedCategoryCode, setSelectedCategoryCode] = useState('');
	/** Counts drafts, so the fields remount for each new contact and come back folded. */
	const [draftKey, setDraftKey] = useState(0);
	const hasTrade = Boolean(selectedCategoryCode);
	const controller = useContactFormController({
		houseId,
		categoryCode: selectedCategoryCode,
		onSuccess: onAdded,
	});
	const resetForm = controller.resetForm;
	const reset = useCallback(() => {
		setSelectedCategoryCode('');
		setDraftKey((key) => key + 1);
		resetForm();
	}, [resetForm]);

	const submit = useCallback(async () => {
		let submitted = false;
		await controller.handleSubmit(async (data) => {
			if (!hasTrade) return;
			submitted = await controller.submitContact(data);
		})();
		if (submitted) reset();
		return submitted;
	}, [controller, hasTrade, reset]);

	return {
		controller,
		draftKey,
		selectedCategoryCode,
		setSelectedCategoryCode,
		canSubmit: hasTrade && controller.isValid,
		isSubmitting: controller.isPending,
		isDirty: hasTrade || controller.isDirty,
		disabledHint: hasTrade ? 'Complete the required contact details' : 'Choose a service',
		submit,
		reset,
	};
};
